/**
 * FamilySwitcher — active-family dropdown for the app header.
 *
 * Lists every family the user belongs to, marks the active one, switches the
 * whole app scope on selection, and links to family settings / creating
 * another family. Hidden while the user has fewer than one family (the
 * FamilyGate handles that case).
 *
 * @module features/family/components/FamilySwitcher
 */

import { Check, ChevronDown, Plus, Settings2, Users } from 'lucide-react';
import { useAuthStore } from '@/features/auth/stores/authStore';
import { Button } from '@/shared/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/shared/components/ui/dropdown-menu';
import { RouteKey } from '@/shared/constants/routes';
import { useAppNavigation } from '@/shared/hooks/useAppNavigation';
import { useToast } from '@/shared/hooks/useToast';
import { cn } from '@/shared/lib/utils';
import { ButtonVariant } from '@/shared/types/button.types';
import { ToastVariant } from '@/shared/types/toast.types';
import { useFamilyStore } from '../stores/familyStore';

export function FamilySwitcher() {
  const { navigateTo } = useAppNavigation();
  const { toast } = useToast();
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const families = useFamilyStore((state) => state.families);
  const activeFamilyId = useFamilyStore((state) => state.activeFamilyId);
  const loading = useFamilyStore((state) => state.loading);
  const switchFamily = useFamilyStore((state) => state.switchFamily);

  if (!isAuthenticated || families.length === 0) {
    return null;
  }

  const activeFamily = families.find((family) => family.id === activeFamilyId);

  const handleSwitch = async (familyId: string): Promise<void> => {
    if (familyId === activeFamilyId) {
      return;
    }
    try {
      await switchFamily(familyId);
      const next = families.find((family) => family.id === familyId);
      toast({
        title: 'Семейството е сменено',
        description: next ? `Активно семейство: „${next.name}“.` : undefined,
      });
      navigateTo({ route: RouteKey.DASHBOARD });
    } catch (error) {
      toast({
        title: 'Грешка',
        description:
          error instanceof Error
            ? error.message
            : 'Смяната на семейство е неуспешна.',
        variant: ToastVariant.Destructive,
      });
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant={ButtonVariant.Ghost}
          className="max-w-[180px] gap-1.5 px-2 sm:max-w-[240px]"
          disabled={loading}
          aria-label="Смени семейство"
        >
          <Users className="size-4 shrink-0" aria-hidden="true" />
          <span className="truncate text-sm font-medium">
            {activeFamily?.name ?? 'Семейство'}
          </span>
          <ChevronDown
            className="size-3.5 shrink-0 text-muted-foreground"
            aria-hidden="true"
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          Моите семейства
        </DropdownMenuLabel>
        {families.map((family) => (
          <DropdownMenuItem
            key={family.id}
            className={cn(
              'cursor-pointer gap-2',
              family.id === activeFamilyId && 'font-medium'
            )}
            onClick={() => void handleSwitch(family.id)}
          >
            <Users className="size-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{family.name}</span>
            {family.id === activeFamilyId && (
              <Check
                className="ml-auto size-4 shrink-0 text-primary"
                aria-hidden="true"
              />
            )}
          </DropdownMenuItem>
        ))}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          className="cursor-pointer gap-2"
          onClick={() => navigateTo({ route: RouteKey.FAMILY_SETTINGS })}
        >
          <Settings2 className="size-4" aria-hidden="true" />
          Управление на семейството
        </DropdownMenuItem>
        <DropdownMenuItem
          className="cursor-pointer gap-2"
          onClick={() => navigateTo({ route: RouteKey.ONBOARDING })}
        >
          <Plus className="size-4" aria-hidden="true" />
          Ново семейство
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
